import {
	FEE_MULTISELECT_OPTIONS,
	FEE_QUICK_OPTIONS,
	CARD_STAMP_QUICK_OPTIONS,
} from 'features/transactions/constants/transactionQuickFilters';
import { FilterModel, FilterType, OperatorFilterEnum, SelectFilterModelMultiple } from 'features/common/types';

export const normalizeMultipleSelectValue = (raw: unknown): number[] => {
	if (Array.isArray(raw)) {
		return raw.map(Number).filter((value) => !Number.isNaN(value));
	}

	if (typeof raw === 'number' && !Number.isNaN(raw)) {
		return [raw];
	}

	if (typeof raw === 'string' && raw.trim()) {
		return raw
			.split(',')
			.map((value) => Number(value.trim()))
			.filter((value) => !Number.isNaN(value));
	}

	return [];
};

export const normalizeTransactionFilters = (filters: FilterModel[]): FilterModel[] =>
	filters
		.map((filter) => {
			if (filter.key !== 'fee' || filter.value === undefined || filter.value === null || filter.value === '') {
				return filter;
			}

			const values = normalizeMultipleSelectValue(filter.value);
			if (values.length === 0) {
				return filter;
			}

			if (filter instanceof SelectFilterModelMultiple && Array.isArray(filter.value)) {
				return filter;
			}

			return createFeeFilter(values);
		})
		.filter((filter) => {
			if (filter.key !== 'fee') return true;
			return normalizeMultipleSelectValue(filter.value).length > 0;
		});

export const getFeeValuesFromFilters = (filters: FilterModel[]): number[] => {
	const feeFilter = filters.find((filter) => filter.key === 'fee');
	if (!feeFilter?.value) return [];

	return normalizeMultipleSelectValue(feeFilter.value).sort((a, b) => a - b);
};

export const getActiveFeePresetId = (filters: FilterModel[]): string | null => {
	const currentValues = getFeeValuesFromFilters(filters);
	if (currentValues.length === 0) return null;

	const match = FEE_QUICK_OPTIONS.find((option) =>
		arraysEqual(
			currentValues,
			[...option.values].sort((a, b) => a - b),
		),
	);
	return match?.id ?? null;
};

export const getActiveCardStampId = (filters: FilterModel[]): string | null => {
	const stampFilter = filters.find((filter) => filter.key === 'cardStamp');
	if (!stampFilter?.value) return null;

	const value = String(stampFilter.value).toLowerCase();
	return (
		CARD_STAMP_QUICK_OPTIONS.find((option) => value === option.apiValue || value.includes(option.apiValue))?.id ?? null
	);
};

export const createCardStampFilter = (apiValue: string): FilterModel => ({
	label: 'Sello',
	type: FilterType.STRING,
	key: 'cardStamp',
	value: apiValue,
	operator: OperatorFilterEnum.CONTAINS,
});

export const createFeeFilter = (values: number[]): SelectFilterModelMultiple =>
	new SelectFilterModelMultiple('Cuotas', 'fee', FEE_MULTISELECT_OPTIONS, values);

export const upsertFilter = (filters: FilterModel[], nextFilter: FilterModel): FilterModel[] => {
	const nextFilters = [...filters];
	const index = nextFilters.findIndex((filter) => filter.key === nextFilter.key);
	if (index !== -1) {
		nextFilters[index] = nextFilter;
	} else {
		nextFilters.push(nextFilter);
	}
	return nextFilters;
};

export const removeFilterByKey = (filters: FilterModel[], key: string): FilterModel[] =>
	filters.filter((filter) => filter.key !== key);

const arraysEqual = (left: number[], right: number[]): boolean =>
	left.length === right.length && left.every((value, index) => value === right[index]);
